import { useEffect, useState, useCallback } from "react";
import CreatableSelect from "react-select/creatable";
import {
    dieMasterAPI,
    supplierDieAPI,
    DiePurchaseOrderAPI,
} from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import InputField from "../../common/InputField";
import {
    MdDeleteOutline,
    MdAdd,
    MdAttachFile
} from "react-icons/md";
import { FaEye } from "react-icons/fa";
import { HiOutlineCheckCircle } from "react-icons/hi";
import { FormHeader, FormFooter } from "../../common/Shared";
import Loader from "../../common/Loader";

const DiePurchaseOrderForm = ({ initialData, onSubmit, onCancel, isEditing, isLoading }) => {
    const today = new Date().toISOString()?.split("T")[0];
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.userId;

    const [formData, setFormData] = useState({
        po_No: 0,
        po_Date: today,
        supplier_Id: "",
        delivery_Days: "",
        delivery_Date: "",
        created_By: userId,
        details: [],
    });

    const [supplierOptions, setSupplierOptions] = useState([]);
    const [dieOptions, setDieOptions] = useState([]);

    useEffect(() => {
        const loadDropdowns = async () => {
            try {
                const [suppliers, dies] = await Promise.all([
                    supplierDieAPI.getAll(),
                    dieMasterAPI.getAll(),
                ]);

                if (suppliers?.data) {
                    setSupplierOptions(
                        suppliers.data.map((s) => ({
                            value: s.supplier_Id,
                            label: s.supplier_Name,
                        }))
                    );
                }

                if (dies?.data) {
                    setDieOptions(
                        dies.data.map((d) => ({
                            value: d.dieNumber,
                            label: d.dieNumber,
                        }))
                    );
                }
            } catch (err) {
                notify("Failed to load dropdowns", "error");
            }
        };
        loadDropdowns();
    }, []);

    useEffect(() => {
        if (initialData) {
            const dataToUse = initialData.data || initialData;
            const activeDetails = dataToUse.details || [];
            setFormData({
                ...dataToUse,
                po_No: dataToUse.po_No || dataToUse.PO_No || dataToUse.pO_No || 0,
                po_Date: (dataToUse.po_Date || dataToUse.pO_Date || dataToUse.PO_Date || "")?.split('T')[0],
                delivery_Date: (dataToUse.delivery_Date || dataToUse.Delivery_Date || "")?.split('T')[0],
                details: activeDetails.length > 0 ? activeDetails.map(d => {
                    // Helper to get value from API response regardless of case
                    const getVal = (key) => {
                        if (!d) return undefined;
                        const foundKey = Object.keys(d).find(k => k.toLowerCase() === key.toLowerCase());
                        return foundKey ? d[foundKey] : undefined;
                    };

                    const wh = getVal('width_height') || "";
                    const [w, h] = wh.split('X');

                    return {
                        trn_No: getVal('trn_no') || 0,
                        dieNumber: getVal('dienumber') || "",
                        subDieNo: getVal('subdieno') || "",
                        profile_Name: getVal('profile_name') || "",
                        series_Name: getVal('series_name') || "",
                        orderWeight: getVal('orderweight') || "",
                        width: w || "",
                        height: h || "",
                        qty: getVal('qty') || 0,
                        rate: getVal('rate') || 0,
                        gst_per: getVal('gst_per') || 18,
                        gst_amount: getVal('gst_amount') || 0,
                        is_Bloster: getVal('is_bloster') || false,
                        bloster_No: getVal('bloster_no') || "",
                        is_Pdf_Upload: getVal('is_pdf_upload') || false,
                        is_Image_Upload: getVal('is_image_upload') || false,
                        is_Inwarded: getVal('is_inwarded') || false,
                        range_Per: getVal('range_per') || "",
                        area_Sqmm: getVal('area_sqmm') || "",
                        perimeter: getVal('perimeter') || ""
                    };
                }) : [{
                    trn_No: 0,
                    dieNumber: "",
                    subDieNo: "",
                    is_Bloster: false,
                    bloster_No: "",
                    file: null,
                    is_Pdf_Upload: false,
                    orderWeight: "",
                    profile_Name: "",
                    series_Name: "",
                    width: "",
                    height: "",
                    die_Image: "",
                    imageFile: null,
                    is_Image_Upload: false,
                    range_Per: "",
                    area_Sqmm: "",
                    perimeter: "",
                    qty: "",
                    rate: "",
                    gst_per: 18,
                    gst_amount: ""
                }]
            });
        }
    }, [initialData]);

    const handleMasterChange = (field, value) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };

            if (field === "po_Date" || field === "delivery_Days") {
                const pDate = field === "po_Date" ? value : prev.po_Date;
                const dDays = field === "delivery_Days" ? value : prev.delivery_Days;

                if (pDate && dDays) {
                    const date = new Date(pDate);
                    date.setDate(date.getDate() + parseInt(dDays));
                    next.delivery_Date = date.toISOString().split('T')[0];
                }
            }
            return next;
        });
    };

    const handleDetailChange = (index, field, value) => {
        setFormData((prev) => {
            const newDetails = [...prev.details];
            newDetails[index] = { ...newDetails[index], [field]: value };

            if (field === "is_Bloster" && !value) {
                newDetails[index].bloster_No = "";
            }

            if (["qty", "rate", "gst_per"].includes(field)) {
                const q = parseFloat(newDetails[index].qty || 0);
                const r = parseFloat(newDetails[index].rate || 0);
                const g = parseFloat(newDetails[index].gst_per || 0);
                const amt = q * r;
                newDetails[index].gst_amount = (amt * g / 100).toFixed(2);
            }
            return { ...prev, details: newDetails };
        });
    };

    const fetchSubDieNo = useCallback(async (dieNo, index) => {
        if (!dieNo) return;
        try {
            const res = await DiePurchaseOrderAPI.getNewSubDieNo(dieNo);
            const subDieNo = res?.subDieNo ?? "";
            setFormData((prev) => {
                const newDetails = [...prev.details];
                newDetails[index] = { ...newDetails[index], subDieNo: subDieNo };
                return { ...prev, details: newDetails };
            });
        } catch (err) {
            console.error("Failed to fetch sub die no", err);
        }
    }, []);

    const handleDieChange = (index, dieNumber) => {
        if (dieNumber) {
            const isDuplicate = formData.details.some((item, i) => i !== index && item.dieNumber === dieNumber);
            if (isDuplicate) {
                notify(`Die Number ${dieNumber} is already added to this order`, "warning");
                return;
            }
        }

        handleDetailChange(index, "dieNumber", dieNumber);
        if (dieNumber) {
            fetchSubDieNo(dieNumber, index);
        } else {
            handleDetailChange(index, "subDieNo", "");
        }
    };

    const handleAddRow = () => {
        setFormData((prev) => ({
            ...prev,
            details: [
                ...prev.details,
                {
                    trn_No: 0,
                    dieNumber: "",
                    subDieNo: "",
                    is_Bloster: false,
                    bloster_No: "",
                    file: null,
                    is_Pdf_Upload: false,
                    orderWeight: "",
                    profile_Name: "",
                    series_Name: "",
                    width: "",
                    height: "",
                    die_Image: "",
                    imageFile: null,
                    is_Image_Upload: false,
                    range_Per: "",
                    area_Sqmm: "",
                    perimeter: "",
                    qty: "",
                    rate: "",
                    gst_per: 18,
                    gst_amount: ""
                },
            ],
        }));
    };

    const handleRemoveRow = async (index) => {
        if (formData.details.length === 1) {
            notify("At least one item is required", "warning");
            return;
        }

        const itemToRemove = formData.details[index];
        if (itemToRemove.trn_No > 0) {
            try {
                await DiePurchaseOrderAPI.deleteDetail(formData.po_No, itemToRemove.trn_No);
                notify("Item deleted successfully", "success");
                setFormData((prev) => ({
                    ...prev,
                    details: prev.details.filter((_, i) => i !== index)
                }));
            } catch (error) {
                notify("Failed to delete item", "error");
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                details: prev.details.filter((_, i) => i !== index)
            }));
        }
    };

    const handleSave = () => {
        const data = new FormData();
        const poNoToSend = formData.po_No ? formData.po_No : 0;
        data.append("PO_No", poNoToSend);
        data.append("PO_Date", formData.po_Date);
        data.append("Supplier_Id", formData.supplier_Id);
        data.append("Delivery_Days", formData.delivery_Days);
        data.append("Delivery_Date", formData.delivery_Date);
        data.append("Created_By", formData.created_By);

        formData.details.forEach((item, index) => {
            data.append(`Details[${index}].Trn_No`, item.trn_No || 0);
            data.append(`Details[${index}].DieNumber`, item.dieNumber);
            data.append(`Details[${index}].SubDieNo`, item.subDieNo || "");
            data.append(`Details[${index}].Is_Bloster`, item.is_Bloster || false);
            if (item.is_Bloster) {
                data.append(`Details[${index}].Bloster_No`, item.bloster_No);
            }

            // Send Is_Pdf_Upload flag to ensure backend knows a file exists/should be kept
            data.append(`Details[${index}].Is_Pdf_Upload`, item.is_Pdf_Upload || false);

            if (item.file instanceof File) {
                data.append(`Details[${index}].File`, item.file);
            }

            data.append(`Details[${index}].OrderWeight`, item.orderWeight || "");
            data.append(`Details[${index}].Profile_Name`, item.profile_Name || "");
            data.append(`Details[${index}].Series_Name`, item.series_Name || "");
            const combinedWH = (item.width || item.height) ? `${item.width || ""}X${item.height || ""}` : "";
            data.append(`Details[${index}].Width_Height`, combinedWH);
            data.append(`Details[${index}].Die_Image`, item.die_Image || "");
            data.append(`Details[${index}].Range_Per`, item.range_Per || "");
            data.append(`Details[${index}].Area_Sqmm`, item.area_Sqmm || "");
            data.append(`Details[${index}].Perimeter`, item.perimeter || "");
            data.append(`Details[${index}].Qty`, item.qty || "");
            data.append(`Details[${index}].Rate`, item.rate || "");
            data.append(`Details[${index}].Gst_Per`, item.gst_per || "");
            data.append(`Details[${index}].Gst_Amount`, item.gst_amount || "");

            const baseAmt = (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
            data.append(`Details[${index}].Amount`, baseAmt.toFixed(2));
            data.append(`Details[${index}].Final_Amount`, (baseAmt + parseFloat(item.gst_amount || 0)).toFixed(2));

            data.append(`Details[${index}].Is_Image_Upload`, item.is_Image_Upload || false);

            if (item.imageFile instanceof File) {
                data.append(`Details[${index}].ImageFile`, item.imageFile);
            }
        });

        onSubmit(data);
    };

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden font-plus relative">
            {isLoading && <Loader centered />}
            <FormHeader
                title={isEditing ? "Edit Purchase Order" : "Add Purchase Order"}
                onCancel={onCancel}
            />

            <div className="overflow-y-auto p-8 bg-white flex-1 scroll-smooth">
                {/* Transaction Metadata */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-4">
                        <span className="w-1.5 h-4 bg-primary"></span>
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                            PO Details
                        </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-10 gap-6">
                        <div className="md:col-span-1">
                            <InputField
                                label="PO No"
                                value={formData.po_No}
                                readOnly
                                placeholder="Auto"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <InputField
                                type="date"
                                label="PO Date"
                                value={formData.po_Date}
                                onChange={(v) => handleMasterChange("po_Date", v)}
                            />
                        </div>
                        <div className="md:col-span-1">
                            <InputField
                                label="Days"
                                type="number"
                                value={formData.delivery_Days}
                                onChange={(v) => handleMasterChange("delivery_Days", v)}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <InputField
                                type="date"
                                label="Delivery Date"
                                value={formData.delivery_Date}
                                readOnly
                            />
                        </div>
                        <div className="md:col-span-4">
                            <InputField
                                type="select"
                                label="Supplier Name"
                                value={formData.supplier_Id}
                                options={supplierOptions}
                                onChange={(s) => handleMasterChange("supplier_Id", s)}
                            />
                        </div>
                    </div>
                </div>

                {/* Line Item Manifest */}
                <div className="">
                    <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-4">
                        <span className="w-1.5 h-4 bg-primary"></span>
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[2.5px]">
                            Order Items <span className="bg-primary text-white px-3 py-1 text-[8px] font-black tracking-widest">{formData.details.length} Items</span>
                        </h3>
                    </div>

                    <div className="space-y-6">
                        {formData.details.map((item, index) => (
                            <div
                                key={index}
                                className="bg-white border border-gray-200 relative overflow-hidden"
                            >
                                <div className="absolute top-0 left-0 w-1 h-full bg-primary" />

                                <div className="p-6">
                                    <div className="flex items-end gap-4 mb-6">
                                        <div className="flex-none">
                                            <div className="bg-gray-100 text-gray-800 w-10 h-10 flex items-center justify-center font-black text-[12px] border border-gray-200">
                                                {index + 1 < 10 ? `0${index + 1}` : index + 1}
                                            </div>
                                        </div>
                                        <div className="w-[200px]">
                                            <label className="text-[10px] font-black text-gray-400 uppercase block mb-1">Asset ID / Code</label>
                                            <CreatableSelect
                                                options={dieOptions}
                                                isDisabled={item.is_Inwarded}
                                                value={item.dieNumber ? { value: item.dieNumber, label: item.dieNumber } : null}
                                                onChange={(selected) => handleDieChange(index, selected?.value || "")}
                                                onCreateOption={(inputValue) => handleDieChange(index, inputValue)}
                                                placeholder="Die No..."
                                                isClearable
                                                formatCreateLabel={(inputValue) => `ADD "${inputValue}"`}
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '38px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        borderRadius: '0px',
                                                        borderColor: '#e2e8f0',
                                                        backgroundColor: item.is_Inwarded ? '#f4f4f5' : 'white',
                                                        boxShadow: 'none',
                                                        borderWidth: '1px'
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </div>
                                        <div className="w-[120px]">
                                            <label className="text-[10px] font-black text-gray-400 uppercase block mb-1 text-center">Sub Die No</label>
                                            <input type="text" value={item.subDieNo} readOnly className={`w-full ${item.is_Inwarded ? 'bg-gray-200' : 'bg-gray-50'} border border-gray-200 px-3 py-2 text-[11px] font-black text-center text-gray-500 h-[38px]`} />
                                        </div>
                                        <div className="flex-1 min-w-[150px]">
                                            <InputField label="Profile" value={item.profile_Name} onChange={(v) => handleDetailChange(index, "profile_Name", v)} placeholder="..." className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="flex-1 min-w-[150px]">
                                            <InputField label="Series" value={item.series_Name} onChange={(v) => handleDetailChange(index, "series_Name", v)} placeholder="..." className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>

                                        <div className="w-[90px]">
                                            <InputField label="QTY" type="number" value={item.qty} onChange={(v) => handleDetailChange(index, "qty", v)} placeholder="0" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="w-[100px]">
                                            <InputField label="RATE" type="number" value={item.rate} onChange={(v) => handleDetailChange(index, "rate", v)} placeholder="0.00" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="w-[60px]">
                                            <InputField label="GST %" type="number" value={item.gst_per} onChange={(v) => handleDetailChange(index, "gst_per", v)} placeholder="18" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="w-[100px]">
                                            <InputField label="GST AMT" value={item.gst_amount || 0} readOnly className="h-[38px] bg-gray-50 font-bold" />
                                        </div>
                                        <div className="w-[120px]">
                                            <InputField label="Total Amt" value={(parseFloat(item.qty * item.rate) + parseFloat(item.gst_amount || 0)).toFixed(2)} readOnly className="h-[38px] bg-primary/5 font-black text-primary" />
                                        </div>

                                        <div className="w-[200px]">
                                            <label className="text-[10px] font-black text-gray-400 uppercase block mb-1 text-center">PDF/Drawing</label>
                                            <div className="flex items-center gap-2 h-[38px]">
                                                <label className={`flex-1 flex items-center justify-center border border-gray-200 border-dashed ${item.is_Inwarded ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer hover:bg-gray-50'} h-full transition-colors`}>
                                                    <MdAttachFile className="text-gray-400 text-lg mr-2" />
                                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">{item.is_Inwarded ? "LOCKED" : item.file ? "MODIFY" : "UPLOAD"}</span>
                                                    {!item.is_Inwarded && <input type="file" className="hidden" accept=".pdf" onChange={(e) => handleDetailChange(index, "file", e.target.files[0])} />}
                                                </label>
                                                {item.is_Pdf_Upload && !item.file && (
                                                    <a href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoPDFFiles/${formData.po_No}_${item.dieNumber}${item.subDieNo ? '_' + item.subDieNo : ''}.pdf`} target="_blank" rel="noopener noreferrer" className="bg-primary text-white w-10 h-[38px] flex items-center justify-center hover:bg-[#2c5ebc]" title="View PDF">
                                                        <FaEye size={16} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>


                                    </div>

                                    <div className="flex items-start gap-4 border-t border-gray-100 pt-6">
                                        <div className="w-[120px]">
                                            <InputField label="Order Weight" type="number" value={item.orderWeight} onChange={(v) => handleDetailChange(index, "orderWeight", v)} placeholder="0.00" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="w-[200px] flex gap-2">
                                            <div className="flex-1">
                                                <InputField label="WIDTH" type="number" value={item.width} onChange={(v) => handleDetailChange(index, "width", v)} placeholder="MM" className="h-[38px]" readOnly={item.is_Inwarded} />
                                            </div>
                                            <div className="flex-1">
                                                <InputField label="HEIGHT" type="number" value={item.height} onChange={(v) => handleDetailChange(index, "height", v)} placeholder="MM" className="h-[38px]" readOnly={item.is_Inwarded} />
                                            </div>
                                        </div>
                                        <div className="w-[120px]">
                                            <InputField label="Area" type="number" value={item.area_Sqmm} onChange={(v) => handleDetailChange(index, "area_Sqmm", v)} placeholder="0" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="w-[100px]">
                                            <InputField label="Range %" type="number" value={item.range_Per} onChange={(v) => handleDetailChange(index, "range_Per", v)} placeholder="0" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>
                                        <div className="flex-1 min-w-[120px]">
                                            <InputField label="PERIMETER" type="number" value={item.perimeter} onChange={(v) => handleDetailChange(index, "perimeter", v)} placeholder="0" className="h-[38px]" readOnly={item.is_Inwarded} />
                                        </div>

                                        <div className="w-[80px] flex flex-col items-center">
                                            <label className="text-[10px] font-black text-gray-400 uppercase block mb-1">Bloster</label>
                                            <div className="h-[38px] flex items-center justify-center">
                                                <input
                                                    type="checkbox"
                                                    disabled={item.is_Inwarded}
                                                    className="w-5 h-5 border-gray-300 accent-primary cursor-pointer"
                                                    checked={item.is_Bloster || false}
                                                    onChange={(e) => handleDetailChange(index, "is_Bloster", e.target.checked)}
                                                />
                                            </div>
                                        </div>

                                        {item.is_Bloster && (
                                            <div className="w-[150px]">
                                                <InputField
                                                    label="Bloster No"
                                                    value={item.bloster_No}
                                                    onChange={(v) => handleDetailChange(index, "bloster_No", v)}
                                                    placeholder="ID"
                                                    className="h-[38px]"
                                                    readOnly={item.is_Inwarded}
                                                />
                                            </div>
                                        )}

                                        <div className="w-[300px] flex flex-col">
                                            <label className="text-[10px] font-black text-gray-400 uppercase block mb-1 text-center">Image</label>
                                            <div className="flex items-center gap-2">
                                                <label className={`w-full h-[38px] flex items-center justify-center border border-gray-200 border-dashed ${item.is_Inwarded ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer hover:bg-gray-50'} px-4 transition-colors`}>
                                                    <MdAdd size={18} className="text-gray-400 mr-2" />
                                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest truncate">{item.is_Inwarded ? "LOCKED" : item.imageFile ? "MODIFY" : "UPLOAD"}</span>
                                                    {!item.is_Inwarded && <input type="file" className="hidden" accept="image/*" onChange={(e) => handleDetailChange(index, "imageFile", e.target.files[0])} />}
                                                </label>
                                                {(item.die_Image || item.is_Image_Upload) && !item.imageFile && (
                                                    <a href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoImageFiles/${formData.po_No}_${item.dieNumber}${item.subDieNo ? '_' + item.subDieNo : ''}.jpg`} target="_blank" rel="noopener noreferrer" className="bg-primary text-white w-10 h-[38px] flex items-center justify-center hover:bg-[#2c5ebc]" title="View Image">
                                                        <FaEye size={16} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 self-end ml-auto">
                                            {!item.is_Inwarded && (
                                                <button
                                                    onClick={handleAddRow}
                                                    className="bg-white border border-gray-200 text-gray-400 hover:text-green-600 hover:border-green-600 w-10 h-[38px] flex items-center justify-center transition-all"
                                                    title="Add New Item"
                                                >
                                                    <MdAdd size={24} />
                                                </button>
                                            )}
                                            <div>
                                                {item.is_Inwarded ? (
                                                    <div className="bg-green-600 text-white px-4 py-2 text-[9px] font-black tracking-widest flex items-center gap-2">
                                                        <HiOutlineCheckCircle size={14} /> INWARDED
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleRemoveRow(index)}
                                                        className="bg-white border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-600 w-10 h-[38px] flex items-center justify-center transition-all"
                                                        title="Remove Item"
                                                    >
                                                        <MdDeleteOutline size={22} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>
            </div>

            <FormFooter
                onCancel={onCancel}
                onSubmit={handleSave}
                isSaving={isLoading}
                submitText={isEditing ? "Update" : "Save"}
            />
        </div>
    );
};

export default DiePurchaseOrderForm;
